import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import AuthLayout from '../../../components/shared/AuthLayout/AuthLayout';
import { confirmNumber, resendConfirmationCode, sendSkipMobileVerification } from '../../../redux/actions/authActions';
import authActionConstants from '../../../redux/constants/authActionConstants';
import urls from '../../../routing/urls';

import styles from './ConfirmationCode.module.scss';

const getRetryTimeout = (attemptNumber) => {
  const retryTimeouts = {
    0: 0,
    1: 30,
    2: 60,
    3: 120,
    4: 240,
    5: 600,
  };
  return retryTimeouts[attemptNumber] || 600;
};

const ConfirmationCode = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentUser, phoneConfirmationData, resendConfirmationCodeLoading, phoneConfirmationCodeSentAttempts } =
    useSelector((state) => state.authReducer);
  const { currentStep } = useSelector((state) => state.stepsReducer);

  const [state, setState] = useState({
    code: ['', '', '', '', '', ''],
    isDisabled: true,
    isResendDisabled: false,
    resendTimer: 0,
  });

  const intervalRef = useRef(null);

  useEffect(
    () => () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    },
    []
  );

  const startResendTimer = useCallback(() => {
    const attemptNumber = phoneConfirmationCodeSentAttempts || 0;
    const timeout = getRetryTimeout(attemptNumber);

    setState((prev) => ({
      ...prev,
      isResendDisabled: true,
      resendTimer: timeout,
    }));

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setState((prev) => {
        if (prev.resendTimer <= 1) {
          clearInterval(intervalRef.current);
          return { ...prev, isResendDisabled: false, resendTimer: 0 };
        }
        return { ...prev, resendTimer: prev.resendTimer - 1 };
      });
    }, 1000);
  }, [phoneConfirmationCodeSentAttempts]);

  useEffect(() => {
    startResendTimer();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [phoneConfirmationCodeSentAttempts, startResendTimer]);

  const validationSchema = Yup.object({
    confirmation_code: Yup.string()
      .required(t('validation:confirmationCode.required'))
      .matches(/^\d{6}$/, t('validation:confirmationCode.exactDigits', { count: 6 })),
  });

  const initialValues = {
    confirmation_code: '',
    phone_number: phoneConfirmationData?.phone_number || currentUser?.user?.phone_number || '',
  };

  const handleCodeChange = (e, index) => {
    const { value } = e.target;

    if (!/^\d*$/.test(value)) return;

    const newCode = [...state.code];
    newCode[index] = value;

    setState((prev) => ({
      ...prev,
      code: newCode,
      isDisabled: newCode.join('').length !== 6,
    }));

    if (value && index < 5) {
      const nextInput = e.target.parentNode.children[index + 1];
      if (nextInput) nextInput.focus();
    }

    if (newCode.join('').length === 6) {
      const formData = {
        confirmation_code: newCode.join(''),
        phone_number: phoneConfirmationData?.phone_number || currentUser?.user?.phone_number || '',
      };

      dispatch(confirmNumber(formData));
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      const prevInput = e.target.parentNode.children[index - 1];
      if (prevInput) {
        prevInput.focus();
        prevInput.select();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();

    const pastedText = (e.clipboardData || window.clipboardData).getData('text');

    const cleanText = pastedText.replace(/\D/g, '');

    if (cleanText.length === 6) {
      const digits = cleanText.split('');

      setState((prev) => ({
        ...prev,
        code: digits,
        isDisabled: false,
      }));

      const lastInput = e.target.parentNode.children[5];
      if (lastInput) {
        lastInput.focus();
        lastInput.select();
      }

      const formData = {
        confirmation_code: cleanText,
        phone_number: phoneConfirmationData?.phone_number || currentUser?.user?.phone_number || '',
      };

      dispatch(confirmNumber(formData));
    } else if (cleanText.length > 6) {
      const digits = cleanText.substring(0, 6).split('');

      setState((prev) => ({
        ...prev,
        code: digits,
        isDisabled: false,
      }));

      const lastInput = e.target.parentNode.children[5];
      if (lastInput) {
        lastInput.focus();
        lastInput.select();
      }

      const formData = {
        confirmation_code: cleanText.substring(0, 6),
        phone_number: phoneConfirmationData?.phone_number || currentUser?.user?.phone_number || '',
      };

      dispatch(confirmNumber(formData));
    }
  };

  const handleConfirmationSubmit = (values) => {
    const formData = {
      confirmation_code: state.code.join(''),
      phone_number: phoneConfirmationData?.phone_number || values.phone_number,
    };

    dispatch(
      confirmNumber(formData, () => {
        navigate(urls.location);
      })
    );
  };

  const handleResendCode = () => {
    dispatch(resendConfirmationCode(initialValues));
    startResendTimer();
  };

  const handleSetupLater = () => {
    dispatch(sendSkipMobileVerification());
    if (currentStep === 'location') {
      navigate(urls.location);
    }
  };

  const handleGoBack = () => {
    dispatch({ type: authActionConstants.CLEAR_PHONE_CONFIRMATION_DATA });
    navigate(urls.confirmPhoneNumber);
  };

  const renderConfirmationForm = () => {
    const { isResendDisabled, resendTimer } = state;

    return (
      <AuthLayout>
        <div className={styles.container}>
          <div className={styles.formWrapper}>
            <div className={styles.header}>
              <h2 className={styles.headerText}>{t('signUp.enterVerificationCode')}</h2>
            </div>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleConfirmationSubmit}
            >
              {() => (
                <Form className={styles.form}>
                  <div className={styles.codeInputWrapper}>
                    <div className={styles.codeInputsContainer}>
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <input
                          key={index}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={1}
                          className={styles.codeInputBar}
                          value={state.code[index] || ''}
                          onChange={(e) => handleCodeChange(e, index)}
                          onKeyDown={(e) => handleKeyDown(e, index)}
                          onFocus={(e) => e.target.select()}
                          onPaste={(e) => handlePaste(e)}
                        />
                      ))}
                    </div>
                  </div>

                  <div className={styles.resendSection}>
                    <p className={styles.resendCode}>
                      {t('signUp.didntGetCode')}{' '}
                      <button
                        type="button"
                        className={`${styles.greenLink} ${isResendDisabled ? styles.disabled : ''}`}
                        onClick={handleResendCode}
                        disabled={isResendDisabled || resendConfirmationCodeLoading}
                      >
                        {isResendDisabled ? t('signUp.resendIn', { seconds: resendTimer }) : t('signUp.resend')}
                      </button>
                      <br />
                      {t('or')}
                      <br />
                      <button type="button" className={styles.greenLink} onClick={handleSetupLater}>
                        {t('signUp.setupLater')}
                      </button>
                    </p>
                  </div>
                </Form>
              )}
            </Formik>
          </div>

          <div className={styles.formBottomWrapper}>
            <p className={styles.formBottomText}>
              {t('signUp.wrongPhoneNumber')}
              <br />
              <button type="button" className={styles.goBackLink} onClick={handleGoBack}>
                <strong>{t('signUp.goBack')}</strong>
              </button>
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  };

  return renderConfirmationForm();
};

export default ConfirmationCode;
